import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { getVehicleDetailUrl } from '../../../lib/routes';
import type { Vehicle } from '../../../lib/vehicles';
import { GuaranteeBadge } from '../../../components/feature/GuaranteeBadge';
import { HonestyScore } from '../../../components/feature/HonestyScore';
import { PriceDropBadge } from '../../../components/feature/PriceDropBadge';
import { RockhoundCertified } from '../../../components/feature/RockhoundCertified';
import { useCompare } from '../../../contexts/CompareContext';
import { thumbnailUrl, FALLBACK_IMAGE } from '../../../lib/imageUtils';

interface VehicleCardProps {
  vehicle: any;
  priority?: boolean;
  onQuickView?: (vehicle: any) => void;
}

function isNewArrival(vehicle: any): boolean {
  const dateField = vehicle.created_at || vehicle.createdAt;
  if (!dateField) return false;
  return new Date(dateField).getTime() >= Date.now() - 30 * 24 * 60 * 60 * 1000;
}

function getViewerCount(vehicleId: string | number): number {
  const seed = String(vehicleId).split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return 2 + ((seed * 9301 + 49297) % 233280 / 233280 * 11 | 0);
}

function getDaysOnLot(vehicle: any): number {
  const dateField = vehicle.created_at || vehicle.createdAt;
  if (!dateField) return 0;
  return Math.floor((Date.now() - new Date(dateField).getTime()) / 86400000);
}

export default function VehicleCard({ vehicle, priority = false, onQuickView }: VehicleCardProps) {
  const [imageError, setImageError] = useState(false);
  const { addToCompare, removeFromCompare, isInCompare, canAddMore } = useCompare();
  const [showTooltip, setShowTooltip] = useState(false);
  const navigate = useNavigate();

  const inCompare = isInCompare(String(vehicle.id));
  const canAdd = canAddMore || inCompare;

  const handleCompareClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (inCompare) {
      removeFromCompare(String(vehicle.id));
    } else {
      const added = addToCompare(String(vehicle.id));
      if (!added) {
        setShowTooltip(true);
        setTimeout(() => setShowTooltip(false), 2000);
      }
    }
  };

  const newArrival = isNewArrival(vehicle);
  const vehicleRouteId = vehicle.uuid || String(vehicle.id);
  const viewerCount = getViewerCount(vehicle.id ?? vehicleRouteId);
  const daysOnLot = getDaysOnLot(vehicle);
  const isAvailable = !vehicle.status || vehicle.status === 'available';

  const monthly = (() => {
    if (!vehicle.price || vehicle.price <= 0) return 0;
    const loan = Math.max(vehicle.price - Math.round(vehicle.price * 0.1), 0);
    const r = 5.9 / 100 / 12;
    const n = 60;
    if (r === 0) return loan / n;
    return (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  })();

  const rawSrc =
    (Array.isArray(vehicle.images) && vehicle.images[0]) ||
    vehicle.image ||
    '';
  const imgSrc = imageError || !rawSrc ? FALLBACK_IMAGE : thumbnailUrl(rawSrc);

  const vehicleLabel = `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim ? ' ' + vehicle.trim : ''}`;
  const applyUrl = `/apply-for-financing?vehicle=${encodeURIComponent(vehicleLabel)}`;
  const detailUrl = `/inventory/${vehicleRouteId}`;

  // Badge config
  const isHotDeal = vehicle.hot_deal && isAvailable;
  const isSold = vehicle.status && vehicle.status !== 'available';

  return (
    <div className="relative group">
      <Link
        to={detailUrl}
        className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3A5F] rounded-2xl"
        aria-label={`View details for ${vehicleLabel} — $${vehicle.price?.toLocaleString()} — ${vehicle.mileage?.toLocaleString()} miles`}
      >
        <article
          className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer border border-[#EDE8DF] hover:border-[#E8B84B]/40 hover:-translate-y-1"
          data-product-shop
        >
          {/* ── Image ── */}
          <div className="relative w-full bg-[#F7F5F0] overflow-hidden" style={{ paddingBottom: '68%' }}>
            <img
              src={imgSrc}
              alt={`${vehicleLabel} — Rockhound Auto`}
              loading={priority ? 'eager' : 'lazy'}
              fetchPriority={priority ? 'high' : 'auto'}
              decoding="async"
              width={400}
              height={272}
              className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
              onError={() => setImageError(true)}
            />

            {/* Dark gradient overlay on hover */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />

            {/* ── TOP-LEFT: Status badge ── */}
            <div className="absolute top-3 left-3 z-20 flex flex-col gap-1.5">
              {isHotDeal && (
                <div className="flex items-center gap-1.5 bg-gradient-to-r from-red-600 to-orange-500 text-white px-3 py-1.5 rounded-lg text-xs font-black tracking-wide shadow-lg"
                  style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: '0.05em' }}>
                  <i className="ri-fire-fill text-sm" aria-hidden="true" />
                  HOT DEAL
                </div>
              )}
              {!isHotDeal && newArrival && (
                <div className="flex items-center gap-1.5 bg-emerald-500 text-white px-3 py-1.5 rounded-lg text-xs font-black tracking-wide shadow-lg"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  <i className="ri-sparkling-2-fill text-sm" aria-hidden="true" />
                  NEW ARRIVAL
                </div>
              )}
              {isSold && (
                <div className="flex items-center gap-1.5 bg-[#1A2332] text-[#E8B84B] px-3 py-1.5 rounded-lg text-xs font-black tracking-wide shadow-lg"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  <i className="ri-checkbox-circle-fill text-sm" aria-hidden="true" />
                  {vehicle.status.toUpperCase()}
                </div>
              )}
            </div>

            {/* ── TOP-RIGHT: Photo count ── */}
            {Array.isArray(vehicle.images) && vehicle.images.length > 1 && (
              <div className="absolute top-3 right-3 z-20 flex items-center gap-1 bg-black/55 backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1 rounded-lg">
                <i className="ri-camera-line text-xs" aria-hidden="true" />
                {vehicle.images.length}
              </div>
            )}

            {/* ── Quick View button ── */}
            {onQuickView && (
              <button
                onClick={e => { e.preventDefault(); e.stopPropagation(); onQuickView(vehicle); }}
                aria-label={`Quick view ${vehicleLabel}`}
                className="absolute bottom-4 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-200 z-30 flex items-center gap-1.5 bg-white text-[#1A2332] px-5 py-2 rounded-full text-xs font-bold shadow-xl hover:bg-[#E8B84B] hover:text-[#1A2332] whitespace-nowrap cursor-pointer pointer-events-auto"
              >
                <i className="ri-zoom-in-line text-sm" aria-hidden="true" />
                Quick View
              </button>
            )}

            {/* ── Viewer count ── */}
            {viewerCount >= 4 && isAvailable && (
              <div className="absolute bottom-12 left-3 z-20 flex items-center gap-1.5 bg-black/65 backdrop-blur-sm text-white px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
                {viewerCount} watching
              </div>
            )}

            {/* ── Dog Rescue strip ── */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/75 to-transparent pt-8 pb-2.5 px-3 z-10">
              <div className="flex items-center justify-center gap-1.5 text-white">
                <i className="ri-heart-fill text-amber-400 text-xs" aria-hidden="true" />
                <span className="text-[11px] font-medium tracking-wide">$100 to Oregon Dog Rescue</span>
              </div>
            </div>
          </div>

          {/* ── Content ── */}
          <div className="p-4">

            {/* Vehicle name */}
            <div className="mb-3">
              <h3 className="font-['Oswald'] text-[1.15rem] font-bold text-[#1A2332] group-hover:text-[#B45309] transition-colors leading-tight tracking-wide">
                {vehicle.year} {vehicle.make} {vehicle.model}
              </h3>
              {vehicle.trim && (
                <p className="text-xs text-[#6B7280] mt-0.5 font-medium">{vehicle.trim}</p>
              )}
            </div>

            {/* ── PRICE BLOCK ── bold, prominent */}
            <div className="mb-3 pb-3 border-b border-[#EDE8DF]">
              <div className="flex flex-col gap-1.5">
                <div className="flex items-end justify-between gap-2 flex-wrap">
                  <div className="flex items-baseline gap-1.5 min-w-0">
                    <span
                      className="text-[1.55rem] font-black text-[#1A2332] leading-none tracking-tight"
                      style={{ fontFamily: "'Oswald', sans-serif", wordBreak: 'break-word' }}
                    >
                      ${vehicle.price?.toLocaleString()}
                    </span>
                  </div>
                  {monthly > 0 && (
                    <Link
                      to={applyUrl}
                      onClick={e => e.stopPropagation()}
                      className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold whitespace-nowrap transition-colors hover:opacity-90 cursor-pointer"
                      style={{ background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D' }}
                      aria-label={`Apply for financing for ${vehicleLabel}`}
                    >
                      <i className="ri-bank-card-line text-xs" aria-hidden="true" />
                      Finance
                    </Link>
                  )}
                </div>
                {monthly > 0 && (
                  <p className="text-xs text-[#B45309] font-semibold" style={{ wordBreak: 'break-word' }}>
                    Est. ${Math.round(monthly).toLocaleString()}/mo · 60 mo @ 5.9%
                  </p>
                )}
              </div>
            </div>

            {/* ── Key Stats ── */}
            <div className="grid grid-cols-2 gap-x-3 gap-y-2 mb-3">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-dashboard-line text-[#B45309] text-sm" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wide leading-none">Mileage</p>
                  <p className="text-xs font-bold text-[#1A2332] mt-0.5 truncate">{vehicle.mileage?.toLocaleString()} mi</p>
                </div>
              </div>
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-gas-station-line text-[#B45309] text-sm" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wide leading-none">Fuel</p>
                  <p className="text-xs font-bold text-[#1A2332] mt-0.5 truncate">{vehicle.fuelType || 'Gas'}</p>
                </div>
              </div>
              {vehicle.transmission && (
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-settings-3-line text-[#B45309] text-sm" aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wide leading-none">Trans.</p>
                    <p className="text-xs font-bold text-[#1A2332] mt-0.5 truncate">{vehicle.transmission}</p>
                  </div>
                </div>
              )}
              {vehicle.drivetrain && (
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-steering-2-line text-[#B45309] text-sm" aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wide leading-none">Drive</p>
                    <p className="text-xs font-bold text-[#1A2332] mt-0.5 truncate">{vehicle.drivetrain}</p>
                  </div>
                </div>
              )}
            </div>

            {/* ── Bottom row: stock + delivery ── */}
            <div className="flex items-center justify-between gap-2 pt-2.5 border-t border-[#EDE8DF]">
              <div className="flex items-center gap-1.5">
                {vehicle.stock_number && (
                  <span className="text-[10px] text-[#9CA3AF] font-mono">#{vehicle.stock_number}</span>
                )}
                {daysOnLot > 0 && (
                  <span className="text-[10px] text-[#9CA3AF]">· {daysOnLot}d on lot</span>
                )}
              </div>
              <Link
                to="/oregon-coast-delivery"
                onClick={e => e.stopPropagation()}
                className="flex items-center gap-1 text-[10px] font-semibold whitespace-nowrap transition-colors hover:text-[#B45309] cursor-pointer"
                style={{ color: '#B45309' }}
                aria-label="Delivery available"
              >
                <i className="ri-truck-line text-xs" aria-hidden="true" />
                Delivery Available
              </Link>
            </div>
          </div>
        </article>
      </Link>

      {/* Contact button — desktop hover-only, hidden on mobile ── */}
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/contact'); }}
        aria-label={`Contact us about the ${vehicleLabel}`}
        className="hidden sm:flex absolute bottom-[4.25rem] right-4 w-9 h-9 items-center justify-center rounded-lg opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D97706] z-10"
        style={{ background: '#1A2332', color: '#E8B84B', border: '1px solid #1A2332' }}
        onMouseEnter={e => {
          const btn = e.currentTarget as HTMLButtonElement;
          btn.style.background = '#E8B84B';
          btn.style.color = '#1A2332';
        }}
        onMouseLeave={e => {
          const btn = e.currentTarget as HTMLButtonElement;
          btn.style.background = '#1A2332';
          btn.style.color = '#E8B84B';
        }}
      >
        <i className="ri-phone-line text-sm" aria-hidden="true" />
      </button>

      {/* Card footer: Compare + mobile Call button ── */}
      <div className="px-4 pb-4 relative">
        <div className="flex gap-2">
          <button
            onClick={handleCompareClick}
            disabled={!canAdd}
            className={`flex-1 py-2.5 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
              inCompare
                ? 'bg-[#E8B84B] text-gray-900 hover:bg-[#d4a73d]'
                : canAdd
                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
            aria-label={inCompare ? 'Remove from compare' : 'Add to compare'}
          >
            <i className={`${inCompare ? 'ri-scales-3-fill' : 'ri-scales-3-line'} text-lg`}></i>
            <span className="text-sm">
              {inCompare ? 'Added' : 'Compare'}
            </span>
          </button>

          {/* Call button — always visible on mobile, hidden on desktop (contact btn handles it via hover) */}
          <a
            href="tel:18554686348"
            onClick={e => e.stopPropagation()}
            aria-label={`Call about ${vehicleLabel}`}
            className="sm:hidden w-11 flex-shrink-0 flex items-center justify-center rounded-lg transition-colors cursor-pointer"
            style={{ background: '#1A2332', color: '#E8B84B', border: '1px solid #1A2332' }}
          >
            <i className="ri-phone-line text-base" aria-hidden="true" />
          </a>
        </div>

        {/* Tooltip for max limit */}
        {showTooltip && (
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap shadow-lg">
            Max 3 vehicles
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
          </div>
        )}
      </div>
    </div>
  );
}
